/**
 * ACTION BUTTONS
 * Boutons d'actions standards pour les pages de liste
 */

import { Columns3, Download, ListFilter, Plus, Search, Upload } from 'lucide-react';
import { type ChangeEvent, type ReactNode } from 'react';

interface ActionButtonsProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onDownload?: () => void;
  onUpload?: () => void;
  onFilter?: () => void;
  onColumns?: () => void;
  onCreate?: () => void;
  activeFiltersCount?: number;
  leadingControls?: ReactNode;
}

export function ActionButtons({
  searchValue,
  onSearchChange,
  onDownload,
  onUpload,
  onFilter,
  onColumns,
  onCreate,
  activeFiltersCount = 0,
  leadingControls,
}: ActionButtonsProps) {
  function handleSearchChange(e: ChangeEvent<HTMLInputElement>) {
    onSearchChange(e.target.value);
  }

  return (
    <>
      {leadingControls}

      {/* Search */}
      <div className="relative w-[17.5rem] h-9 bg-white rounded-md border border-zinc-200 shadow-xs">
        <div className="flex items-center gap-1 h-full px-3 py-1">
          <Search className="size-4 text-zinc-500" />
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Rechercher"
            className="flex-grow text-sm leading-5 text-zinc-900 bg-transparent border-none outline-none min-w-0"
          />
        </div>
      </div>

      {/* Download CSV */}
      {onDownload && (
        <button
          onClick={onDownload}
          className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
          aria-label="Télécharger CSV"
        >
          <Download className="size-4 text-zinc-900" />
        </button>
      )}

      {/* Upload CSV */}
      {onUpload && (
        <button
          onClick={onUpload}
          className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
          aria-label="Importer CSV"
        >
          <Upload className="size-4 text-zinc-900" />
        </button>
      )}

      {/* Filters */}
      {onFilter && (
        <button
          onClick={onFilter}
          className="relative flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
          aria-label="Filtres"
        >
          <ListFilter className="size-4 text-zinc-900" />
          {activeFiltersCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 bg-zinc-900 text-white text-[0.625rem] font-semibold rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </button>
      )}

      {/* Columns */}
      {onColumns && (
        <button
          onClick={onColumns}
          className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
          aria-label="Colonnes"
        >
          <Columns3 className="size-4 text-zinc-900" />
        </button>
      )}

      {/* Create */}
      {onCreate && (
        <button
          onClick={onCreate}
          className="flex items-center justify-center size-9 bg-zinc-900 rounded-md shadow-sm hover:bg-zinc-800 transition-colors"
          aria-label="Créer"
        >
          <Plus className="size-4 text-white" />
        </button>
      )}
    </>
  );
}
